<template>
  <Modal
    title="Edit caption"
    :width="520"
    submit-btn-text="Apply"
    :cancel-button="true"
    :saving="saving"
    :disable-submit="saving"
    @submit="handleApply"
    @hide-modal="emit('hide-modal')"
  >
    <div class="bulk-warn" data-cy="bulk-caption-warning">
      <i class="fa-solid fa-triangle-exclamation"></i>
      <span>This will overwrite any existing caption for {{ summary }}.</span>
    </div>

    <label class="bulk-label" for="bulk-caption-input">Caption</label>
    <textarea
      id="bulk-caption-input"
      v-model="draft"
      class="bulk-input"
      :maxlength="maxLength"
      placeholder="Describe what these show…"
      data-cy="bulk-caption-input"
    ></textarea>
    <div
      class="bulk-count"
      :class="{ near: nearLimit }"
      data-cy="bulk-caption-count"
    >
      {{ draft.length }} / {{ maxLength }}
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import Modal from "@/components/Modal.vue";

const props = withDefaults(
  defineProps<{
    photoCount?: number;
    videoCount?: number;
    saving?: boolean;
    maxLength?: number;
  }>(),
  {
    photoCount: 0,
    videoCount: 0,
    saving: false,
    maxLength: 300
  }
);

const emit = defineEmits<{
  apply: [caption: string];
  "hide-modal": [];
}>();

const draft = ref("");
const nearLimit = computed(() => props.maxLength - draft.value.length <= 100);

function pluralize(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

const summary = computed(() => {
  const parts: string[] = [];
  if (props.photoCount > 0) parts.push(pluralize(props.photoCount, "photo"));
  if (props.videoCount > 0) parts.push(pluralize(props.videoCount, "video"));
  return parts.join(" and ");
});

function handleApply() {
  emit("apply", draft.value.trim());
}
</script>

<style scoped>
.bulk-warn {
  @apply flex items-start gap-2 mb-4 p-3 rounded-md text-sm text-amber-800;
  @apply bg-amber-50 border border-amber-200;
}

.bulk-label {
  @apply block text-sm font-semibold text-gray-900 mb-1;
}

.bulk-input {
  @apply w-full rounded-md border border-gray-300 p-3 text-sm;
  min-height: 120px;
  resize: vertical;
}

.bulk-input:focus {
  @apply outline-none border-blue-600 ring-1 ring-blue-600;
}

.bulk-count {
  @apply text-xs text-gray-500 mt-2;
}

.bulk-count.near {
  @apply text-amber-600;
}
</style>
